import type { DateStyle, PreferenceValues } from '../contract/types';
import {
  dateTimeFormat,
  durationFormat,
  listFormat,
  numberFormat,
  type RangeFormatter,
  relativeTimeFormat,
} from './intl-cache';
import { phraseIn } from './phrases';

/**
 * Dates, times, elapsed time and durations.
 *
 * Everything here reads the time zone from the resolved preferences, so a fleet manager in Detroit
 * and one in Paris looking at the same charge session see their own wall clocks. The zone is passed
 * to `Intl` as a string; no date library is involved, and none is needed.
 *
 * The one place this departs from letting `Intl` decide is field order, and it departs deliberately:
 * a preference that says `MDY` has to beat the locale that says otherwise, or the setting means
 * nothing. Separators are still the locale's own, so a Canadian who asks for month-first gets
 * `08-04-2026` rather than an American's slashes.
 */

export type DateTimeOptions = {
  /** Appends the zone abbreviation, as in `3:48 PM EDT`. */
  readonly withZone?: boolean;
  /** Adds the seconds, as in `3:48:07 PM`, for a time a reader compares against a recording or a log. */
  readonly withSeconds?: boolean;
};

export type DateFormatOptions = {
  /** The year width for a numeric date. Defaults to the full year. */
  readonly year?: 'numeric' | '2-digit';
};

/** Which field goes where, by style code. The whole `DATE` vocabulary in `Formatting.java`. */
const DATE_FIELD_ORDER: Readonly<
  Record<DateStyle, readonly ('day' | 'month' | 'year')[]>
> = {
  MDY: ['month', 'day', 'year'],
  DMY: ['day', 'month', 'year'],
  YMD: ['year', 'month', 'day'],
};

/** Visible stand-in when `Intl` would throw `RangeError` on a non-finite instant. */
const INVALID_DATE = 'Invalid Date';

const isFiniteDate = (value: Date): boolean => Number.isFinite(value.getTime());

/** The separator for a locale `Intl` renders without one, which none currently does. */
const FALLBACK_DATE_SEPARATOR = '/';

const DATE_FIELDS: ReadonlySet<string> = new Set(['day', 'month', 'year']);

/**
 * The punctuation `Intl` puts between and after the three fields.
 *
 * Only the literals sitting between the first and last field are separators; one trailing the last
 * field is part of the pattern rather than a join, and hu-HU and ko-KR both have one — `2026. 08.
 * 04.` loses its final stop if the whole run is treated as separators.
 */
const punctuationOf = (
  parts: readonly Intl.DateTimeFormatPart[]
): { readonly separator: string; readonly suffix: string } => {
  const fieldAt = parts.map(({ type }) => DATE_FIELDS.has(type));
  const first = fieldAt.indexOf(true);
  const last = fieldAt.lastIndexOf(true);

  if (first === -1) return { separator: FALLBACK_DATE_SEPARATOR, suffix: '' };

  const separator =
    parts.slice(first, last).find(({ type }) => type === 'literal')?.value ??
    FALLBACK_DATE_SEPARATOR;

  const suffix = parts
    .slice(last + 1)
    .map(({ value }) => value)
    .join('');

  return { separator, suffix };
};

/**
 * The date, with the user's field order and the locale's punctuation.
 *
 * Assembled from `formatToParts` rather than from a pattern string: asking `Intl` for the numeric
 * date and rearranging what comes back means the separator, the numbering system and the zone
 * conversion are all still CLDR's answers, and only the order is ours.
 *
 * @param preferences - Supplies the locale, the time zone and the date style.
 * @param value - The instant to render.
 * @returns The formatted date, such as `08/04/2026`.
 */
export const formatDate = (
  preferences: PreferenceValues,
  value: Date,
  options?: DateFormatOptions
): string => {
  if (!isFiniteDate(value)) return INVALID_DATE;

  const parts = dateTimeFormat(preferences.locale, {
    year: options?.year ?? 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: preferences.timeZone,
  }).formatToParts(value);

  const punctuation = punctuationOf(parts);

  return (
    DATE_FIELD_ORDER[preferences.formats.date]
      .map((field) => parts.find(({ type }) => type === field)?.value ?? '')
      .join(punctuation.separator) + punctuation.suffix
  );
};

/**
 * The date with its month spelled out: `September 14, 2026`, `14 septembre 2026`.
 *
 * For a heading or a single prominent date, where a row of digits reads as a code and a named month
 * reads as a date. Narrow enough that a table column should still use {@link formatDate}.
 *
 * The field order here is CLDR's rather than the reader's `date` preference, which is the one place
 * in this file that preference does not win. It cannot: en-CA asks for year-first, and `2026
 * September 14` is a shape no locale writes. A spelled month carries its own order, so there is
 * nothing left for the preference to disambiguate.
 *
 * @param preferences - Supplies the locale and the time zone.
 * @param value - The instant to render.
 * @returns The formatted date, such as `September 14, 2026`.
 */
export const formatDateLong = (
  preferences: PreferenceValues,
  value: Date
): string => {
  if (!isFiniteDate(value)) return INVALID_DATE;

  return dateTimeFormat(preferences.locale, {
    dateStyle: 'long',
    timeZone: preferences.timeZone,
  }).format(value);
};

/**
 * The token for each field, in the dialect Luxon, date-fns and CLDR all read the same way.
 *
 * That agreement is what lets one string serve every consumer. It holds for these three tokens and
 * is not general: `EEEE` and `a` diverge, so a pattern that grew a weekday or a meridiem could no
 * longer be handed to an arbitrary reader.
 */
const DATE_FIELD_TOKEN: Readonly<Record<'day' | 'month' | 'year', string>> = {
  day: 'dd',
  month: 'MM',
  year: 'yyyy',
};

/** Any instant serves: only the literals `Intl` puts between the fields are read back. */
const PATTERN_REFERENCE = new Date(Date.UTC(2026, 7, 4));

/** Letters are tokens to every reader, and an apostrophe opens a quoted run in most. */
const AMBIGUOUS_IN_A_PATTERN = /['A-Za-z]/;

/**
 * Quotes a run of punctuation only when leaving it bare would read as tokens.
 *
 * Quoting unconditionally would be safer to reason about and wrong in practice: it turns the en-US
 * pattern into `MM'/'dd'/'yyyy`, which formats the same and no longer equals the `MM/dd/yyyy` the
 * app has always passed around. Slashes, stops and CJK date marks are literal to every reader, so
 * they are left alone and only letters are wrapped.
 */
const asLiteral = (punctuation: string): string =>
  AMBIGUOUS_IN_A_PATTERN.test(punctuation)
    ? `'${punctuation.replace(/'/g, "''")}'`
    : punctuation;

/**
 * The pattern {@link formatDate} renders by, for the components that need one rather than a string.
 *
 * A date picker has to be told the shape of what it displays and reparse what the user picks, and
 * it cannot infer either from formatted output. Derived from the same field order and the same
 * locale punctuation the formatter uses, so the two cannot disagree about what a date looks like.
 *
 * @param preferences - Supplies the locale and the date style.
 * @returns The pattern, such as `MM/dd/yyyy` or `dd.MM.yyyy`.
 */
export const datePatternOf = (preferences: PreferenceValues): string => {
  const parts = dateTimeFormat(preferences.locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: preferences.timeZone,
  }).formatToParts(PATTERN_REFERENCE);

  const { separator, suffix } = punctuationOf(parts);

  return (
    DATE_FIELD_ORDER[preferences.formats.date]
      .map((field) => DATE_FIELD_TOKEN[field])
      .join(asLiteral(separator)) + asLiteral(suffix)
  );
};

/**
 * Options for the clock the user chose.
 *
 * `hour: 'numeric'` on the twelve-hour side is what gives `3:48 PM` rather than `03:48 PM`, matching
 * what the app renders today. `hourCycle: 'h23'` on the other is chosen over `hour12: false`, which
 * has a legacy reading that puts midnight at `24:00`.
 */
const clockOptions = (
  preferences: PreferenceValues
): Intl.DateTimeFormatOptions =>
  preferences.formats.time === '12H'
    ? { hour: 'numeric', minute: '2-digit', hour12: true }
    : { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

/**
 * The time of day.
 *
 * A locale with no twelve-hour convention will render an English `PM` when the preference asks for
 * one — fr-FR gives `3:48 PM`. That is the cost of letting an explicit preference win, and it only
 * reaches a user who asked for a clock their locale does not use.
 *
 * @param preferences - Supplies the locale, the time zone and the clock.
 * @param value - The instant to render.
 * @param options - Whether to name the zone and whether to show the seconds.
 * @returns The formatted time, such as `3:48 PM`, or `3:48:07 PM` with the seconds.
 */
export const formatTime = (
  preferences: PreferenceValues,
  value: Date,
  options?: DateTimeOptions
): string => {
  if (!isFiniteDate(value)) return INVALID_DATE;

  return dateTimeFormat(preferences.locale, {
    ...clockOptions(preferences),
    ...(options?.withSeconds ? { second: '2-digit' as const } : {}),
    timeZone: preferences.timeZone,
    ...(options?.withZone ? { timeZoneName: 'short' as const } : {}),
  }).format(value);
};

/**
 * Date and time together.
 *
 * Composed from the two above rather than from the `DATETIME` style code, because that vocabulary
 * has no year-first variant while `DATE` does — a Japanese or Canadian user has no faithful combined
 * code. Composing keeps `date` and `time` authoritative and gives every combination of the two.
 *
 * The two halves are joined with a plain space. `Intl` would supply a locale-specific word here —
 * English `at`, French `à` — but only when it also chooses the field order, which is the thing this
 * module exists to override.
 *
 * @param preferences - Supplies the locale, the time zone and both styles.
 * @param value - The instant to render.
 * @param options - Whether to name the zone.
 * @returns The formatted timestamp, such as `08/04/2026 3:48 PM`.
 */
export const formatDateTime = (
  preferences: PreferenceValues,
  value: Date,
  options?: DateTimeOptions
): string => {
  if (!isFiniteDate(value)) return INVALID_DATE;

  return `${formatDate(preferences, value)} ${formatTime(preferences, value, options)}`;
};

/**
 * What the reader's time zone is called, on its own.
 *
 * A column of times that all share a zone names it once in the header rather than on every row, and
 * a sentence about a schedule names it in prose. Both need the label without a time attached to it.
 *
 * An instant is still required, because the name depends on it: Detroit is `EST` in January and
 * `EDT` in July, and a label fixed at one of them is wrong for half the year. It defaults to now,
 * which is what a header describing a live column wants.
 *
 * The answer is CLDR's, which means it is an abbreviation only where the locale has one. fr-CA
 * abbreviates Detroit as `HNE`, and es-MX does not abbreviate it at all — it says `GMT-5`. That is
 * the honest label for a reader of that locale, not a gap to paper over with English.
 *
 * @param preferences - Supplies the locale and the time zone.
 * @param value - The instant whose offset decides between standard and daylight naming.
 * @returns The zone's short name, such as `EST`.
 */
export const formatZoneName = (
  preferences: PreferenceValues,
  value: Date = new Date()
): string => {
  if (!isFiniteDate(value)) return INVALID_DATE;

  return (
    dateTimeFormat(preferences.locale, {
      timeZone: preferences.timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(value)
      .find(({ type }) => type === 'timeZoneName')?.value ??
    preferences.timeZone
  );
};

/**
 * A calendar day, in the reader's field order and the locale's punctuation, with no zone applied.
 *
 * A day is not an instant, and converting it into one is the only thing that can go wrong with it.
 * `2026-01-15` names the fifteenth to everyone who reads it; read as midnight UTC and rendered in
 * Detroit it becomes the fourteenth, so a contract start, an invoice or an expiry lands a day early
 * for every reader west of Greenwich. Rendered in UTC the day survives intact, and the field order
 * is still the reader's own.
 *
 * @param preferences - Supplies the locale and the date style. The zone is deliberately unused.
 * @param value - Midnight UTC on the day to render.
 * @returns The formatted day, such as `01/15/2026`.
 */
export const formatPlainDate = (
  preferences: PreferenceValues,
  value: Date,
  options?: DateFormatOptions
): string => formatDate({ ...preferences, timeZone: 'UTC' }, value, options);

/**
 * A calendar day's month and day alone, for a label too narrow to carry the year — a chart axis,
 * where the year is the same across every tick and is shown once elsewhere.
 *
 * Follows the reader's field order with the year dropped, so a day-first reader gets the day first,
 * and keeps the locale's own separator. Unpadded, since a tick reads `8/4`, not `08/04`. Rendered in
 * UTC for the reason {@link formatPlainDate} is.
 *
 * @param preferences - Supplies the locale and the date style. The zone is deliberately unused.
 * @param value - Midnight UTC on the day to render.
 * @returns The formatted month and day, such as `8/4`.
 */
export const formatPlainMonthDay = (
  preferences: PreferenceValues,
  value: Date
): string => {
  if (!isFiniteDate(value)) return INVALID_DATE;

  const parts = dateTimeFormat(preferences.locale, {
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(value);

  const { separator, suffix } = punctuationOf(parts);

  return (
    DATE_FIELD_ORDER[preferences.formats.date]
      .filter((field) => field !== 'year')
      .map((field) => parts.find(({ type }) => type === field)?.value ?? '')
      .join(separator) + suffix
  );
};

/**
 * {@link formatDateLong} for a calendar day rather than an instant, holding the day intact for the
 * same reason {@link formatPlainDate} does.
 *
 * @param preferences - Supplies the locale. The zone is deliberately unused.
 * @param value - Midnight UTC on the day to render.
 * @returns The formatted day, such as `January 15, 2026`.
 */
export const formatPlainDateLong = (
  preferences: PreferenceValues,
  value: Date
): string => formatDateLong({ ...preferences, timeZone: 'UTC' }, value);

/** An ISO-8601 local time: `08:00`, `14:15:00`. A lone hour is accepted; a lone minute is not. */
const PLAIN_TIME = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Any day serves, since the rendering is in UTC and reads only the clock fields off it. The epoch,
 * because UTC has never observed daylight saving and so no day is a special case.
 */
const PLAIN_TIME_DAY = [1970, 0, 1] as const;

/**
 * A time of day that belongs to no particular day: a recurring departure, an opening hour, the
 * bounds of a quiet-hours window.
 *
 * Read from the string the schedule endpoint sends, because that is the shape the fact has. The
 * alternative is what the app does today — inventing an anchor date to have an instant to format,
 * which produces a value that looks like a moment, sorts like a moment and is not one. A recurring
 * `08:00` has no zone and no date, and pinning either to it is how it drifts by an hour in March.
 *
 * Rendered on the reader's clock, so a 24-hour preference sees `08:00` and a 12-hour one `8:00 AM`.
 * No zone is applied and none is named: there is no instant here to place in one.
 *
 * @param preferences - Supplies the locale and the clock. The zone is deliberately unused.
 * @param value - `HH:mm` or `HH:mm:ss`, in the 24-hour clock the payload is written in.
 * @returns The formatted time, such as `8:00 AM`. A string that is not a time of day, or one whose
 *   fields fall outside a real clock, is handed back untouched, as {@link formatIsoDuration} does.
 */
export const formatPlainTime = (
  preferences: PreferenceValues,
  value: string
): string => {
  const matched = PLAIN_TIME.exec(value.trim());

  if (!matched) return value;

  const [hour, minute, second] = [
    Number(matched[1]),
    Number(matched[2]),
    Number(matched[3] ?? 0),
  ];

  // `24:00` is a legal ISO end-of-day and is rejected all the same: on a twelve-hour clock it would
  // render as `12:00 AM`, which a reader takes for the start of the day rather than the end of it.
  if (hour > 23 || minute > 59 || second > 59) return value;

  return formatTime(
    { ...preferences, timeZone: 'UTC' },
    new Date(Date.UTC(...PLAIN_TIME_DAY, hour, minute, second))
  );
};

/**
 * A pair thirteen months apart, so no field is shared and `Intl` has nothing to collapse.
 *
 * Collapsing is the reason the separator is read off a reference pair rather than off the values
 * being rendered: de-DE renders the tenth to the thirty-first of one month as `10.–31.05.2026`,
 * whose punctuation describes a shape this module does not produce. Two dates that share nothing
 * force the full `{0} – {1}` form, which is the join being asked for.
 */
const RANGE_REFERENCE: readonly [Date, Date] = [
  new Date(Date.UTC(2026, 7, 4)),
  new Date(Date.UTC(2027, 8, 6)),
];

/**
 * Thin space, en dash, thin space — what CLDR puts between the ends of a range in most locales, for
 * an engine whose `Intl.DateTimeFormat` has no range half to read it from.
 */
const FALLBACK_RANGE_SEPARATOR = '\u2009\u2013\u2009';

/**
 * What the locale puts between the ends of a range.
 *
 * An en dash and two spaces in most locales, a wave dash and none in ja-JP. Taken from CLDR rather
 * than written down here, for the same reason the date separator is: it is the locale's to decide.
 */
const rangeSeparatorOf = (preferences: PreferenceValues): string => {
  const formatter: Intl.DateTimeFormat & RangeFormatter = dateTimeFormat(
    preferences.locale,
    { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC' }
  );

  const shared = formatter
    .formatRangeToParts?.(...RANGE_REFERENCE)
    .find(({ type, source }) => type === 'literal' && source === 'shared');

  return shared?.value ?? FALLBACK_RANGE_SEPARATOR;
};

/**
 * Both ends of a range, joined the way the locale joins them.
 *
 * Composed from {@link formatDate} rather than handed to `Intl.formatRange`, because the range API
 * chooses its own field order and this module exists to override that. The cost is that a range
 * inside one month renders in full — `05/10/2026 – 05/31/2026` where CLDR would say `05/10 – 31` —
 * and that is the right cost: a reader comparing two dates loses nothing by seeing both of them,
 * and a preference that says month-first has to hold on both ends.
 *
 * @param preferences - Supplies the locale, the time zone and the date style.
 * @param start - The instant the range opens.
 * @param end - The instant it closes.
 * @returns The formatted range, such as `05/10/2026 – 05/31/2026`.
 */
export const formatDateRange = (
  preferences: PreferenceValues,
  start: Date,
  end: Date
): string => {
  if (!isFiniteDate(start) || !isFiniteDate(end)) return INVALID_DATE;

  return [formatDate(preferences, start), formatDate(preferences, end)].join(
    rangeSeparatorOf(preferences)
  );
};

/**
 * Both ends of a span of calendar days, joined the way the locale joins a range.
 *
 * {@link formatPlainDate} for a pair, and for the same reason. A term running `2026-01-15` to
 * `2027-01-15` names two days and no instants, so converting either end into the reader's zone
 * moves a boundary the endpoint never placed — and moves it in exactly the zones west of UTC where
 * a reviewer in Detroit would see it and a reviewer in Berlin would not.
 *
 * @param preferences - Supplies the locale and the date style. The time zone is ignored.
 * @param start - Midnight UTC on the day the span opens.
 * @param end - Midnight UTC on the day it closes.
 * @returns The formatted span, such as `01/15/2026 – 01/15/2027`.
 */
export const formatPlainDateRange = (
  preferences: PreferenceValues,
  start: Date,
  end: Date
): string => formatDateRange({ ...preferences, timeZone: 'UTC' }, start, end);

/**
 * Both ends of a span of the clock, joined the way the locale joins a range.
 *
 * @param preferences - Supplies the locale, the time zone and the clock.
 * @param start - The instant the span opens.
 * @param end - The instant it closes.
 * @param options - Whether to name the zone and whether to show the seconds. The zone is named once,
 *   after the second time, since both ends are read in the same zone and repeating it says nothing.
 *   The seconds apply to both ends.
 * @returns The formatted span, such as `8:00 AM – 9:00 PM EDT`.
 */
export const formatTimeRange = (
  preferences: PreferenceValues,
  start: Date,
  end: Date,
  options?: DateTimeOptions
): string => {
  if (!isFiniteDate(start) || !isFiniteDate(end)) return INVALID_DATE;

  return [
    formatTime(preferences, start, { withSeconds: options?.withSeconds }),
    formatTime(preferences, end, options),
  ].join(rangeSeparatorOf(preferences));
};

/**
 * How long each unit lasts, and how far it reaches before the next one takes over.
 *
 * Elapsed rather than calendar: something eighteen hours old reads as `18 hours ago`, not
 * `yesterday`. That suits what this product shows — telemetry, charge sessions, last-seen times —
 * where the interval is the fact and the calendar boundary is not.
 */
const ELAPSED_UNITS = [
  { unit: 'minute', seconds: 60, upTo: 45 * 60 },
  { unit: 'hour', seconds: 3600, upTo: 22 * 3600 },
  { unit: 'day', seconds: 86400, upTo: 6 * 86400 },
  { unit: 'week', seconds: 604800, upTo: 26 * 86400 },
  { unit: 'month', seconds: 2629800, upTo: 320 * 86400 },
] as const satisfies readonly {
  unit: Intl.RelativeTimeFormatUnit;
  seconds: number;
  upTo: number;
}[];

/** Beyond the last threshold above. Named rather than given an infinite bound of its own. */
const YEARS = { unit: 'year', seconds: 31557600 } as const satisfies {
  unit: Intl.RelativeTimeFormatUnit;
  seconds: number;
};

/** Under this, the answer is `now` rather than a count of seconds nobody asked for. */
const JUST_NOW_SECONDS = 45;

/**
 * How long ago something happened, in words.
 *
 * @param preferences - Supplies the locale.
 * @param value - The instant that elapsed.
 * @param now - The instant to measure from. Defaults to the current time; pass one in tests.
 * @returns A phrase such as `2 hours ago`, `yesterday` or `now`.
 */
export const formatRelativeTime = (
  preferences: PreferenceValues,
  value: Date,
  now: Date = new Date()
): string => {
  if (!isFiniteDate(value) || !isFiniteDate(now)) return INVALID_DATE;

  const elapsed = (value.getTime() - now.getTime()) / 1000;
  const formatter = relativeTimeFormat(preferences.locale, {
    numeric: 'auto',
  });

  if (Math.abs(elapsed) < JUST_NOW_SECONDS)
    return formatter.format(0, 'second');

  const scale =
    ELAPSED_UNITS.find(({ upTo }) => Math.abs(elapsed) < upTo) ?? YEARS;
  const count = elapsed / scale.seconds;
  // Half-away-from-zero so 36h ago and 36h ahead round to the same magnitude.
  const rounded = Math.sign(count) * Math.round(Math.abs(count));

  return formatter.format(
    rounded,
    // `numeric: 'auto'` is what turns -1 day into `yesterday` rather than `1 day ago`.
    scale.unit
  );
};

/**
 * An instant with both halves, for reading a locale's joiner off. Fixed, so the derivation does not
 * depend on when it runs.
 */
const JOINER_REFERENCE = new Date(Date.UTC(2026, 7, 4, 15, 48));

/** The parts belonging to the time half of a formatted timestamp. */
const TIME_PART_TYPES: ReadonlySet<string> = new Set([
  'hour',
  'minute',
  'second',
  'dayPeriod',
]);

/** For a locale whose full timestamp somehow carries no literal between its halves. */
const FALLBACK_JOINER = ' ';

/**
 * The joiner is a property of the locale, not of the instant, so a last-seen column should not
 * re-derive it per cell. Locales are a closed set; the map stays small without an eviction bound.
 */
const JOINER_BY_LOCALE = new Map<string, string>();

/**
 * What this locale puts between a date and a time: ` at ` in English, ` à ` in French, `, ` in
 * Spanish, ` um ` in German.
 *
 * Read off a formatted timestamp rather than tabulated, like every other convention here.
 * {@link formatDateTime} deliberately does not use it — asking ICU to join the halves means letting
 * ICU order the date's fields, which is the thing this module exists to override. Below there is no
 * date to order, because a calendar word has taken its place, so the locale's own joiner is
 * available at no cost.
 */
const dateTimeJoinerOf = (locale: string): string => {
  const cached = JOINER_BY_LOCALE.get(locale);

  if (cached !== undefined) return cached;

  const parts = dateTimeFormat(locale, {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).formatToParts(JOINER_REFERENCE);

  const firstTimePart = parts.findIndex(({ type }) =>
    TIME_PART_TYPES.has(type)
  );
  const preceding = firstTimePart > 0 ? parts[firstTimePart - 1] : undefined;
  const joiner =
    preceding?.type === 'literal' ? preceding.value : FALLBACK_JOINER;

  JOINER_BY_LOCALE.set(locale, joiner);
  return joiner;
};

/**
 * The calendar day an instant falls on in a zone, as `yyyy-mm-dd`.
 *
 * A fixed locale, because the caller wants the day rather than a rendering of it: the numeric parts
 * are the same whoever is reading, and pinning the tag keeps a reader whose locale uses another
 * calendar system from changing which day this reports.
 */
const dayKeyIn = (timeZone: string, value: Date): string => {
  const parts = dateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
    calendar: 'iso8601',
  }).formatToParts(value);

  const field = (type: string): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${field('year')}-${field('month')}-${field('day')}`;
};

/** How many days apart two day keys are. Calendar arithmetic, so no hour is ever 23 or 25 long. */
const daysBetween = (from: string, to: string): number => {
  const asUtc = (key: string): number => {
    const [year, month, day] = key.split('-').map(Number);
    return Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1);
  };

  return Math.round((asUtc(to) - asUtc(from)) / 86400000);
};

/** Beyond this many days either side, a calendar word is less use than the date itself. */
const CALENDAR_WORD_RANGE = 1;

/**
 * The reader's own language decides what an initial capital is — `i` uppercases to `İ` in Turkish —
 * so the locale is passed rather than left to the host's.
 */
const capitaliseIn = (locale: string, text: string): string =>
  text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);

/**
 * A time of day, labelled by the day it falls on: `Today at 8:00 AM`, `Yesterday at 3:00 PM`.
 *
 * The tier between {@link formatRelativeTime} and {@link formatDateTime}. `6 hours ago` has lost
 * the wall clock by the time a reader wants to compare it against their own, and `07/15/2026 6:00
 * AM` makes them work out that it was this morning; this says both at once.
 *
 * Only today, yesterday and tomorrow get a word — past that the calendar phrase stops being shorter
 * than the date it replaces, and `in 5 days at 8:00 AM` is harder to read than the date. Those fall
 * back to the date, still joined by the locale's own word, so the shape of the output never changes
 * even though its first half does.
 *
 * Both halves come from CLDR: the word from `Intl.RelativeTimeFormat` with `numeric: 'auto'`, the
 * joiner from a formatted timestamp. Nothing here is a translated string the package maintains.
 *
 * @param preferences - Supplies the locale, the time zone and the clock.
 * @param value - The instant to render.
 * @param now - The instant that decides which day is today. Defaults to the current time.
 * @returns The labelled time, such as `Today at 8:00 AM`.
 */
export const formatCalendarTime = (
  preferences: PreferenceValues,
  value: Date,
  now: Date = new Date()
): string => {
  if (!isFiniteDate(value) || !isFiniteDate(now)) return INVALID_DATE;

  const offset = daysBetween(
    dayKeyIn(preferences.timeZone, now),
    dayKeyIn(preferences.timeZone, value)
  );

  const head =
    Math.abs(offset) <= CALENDAR_WORD_RANGE
      ? capitaliseIn(
          preferences.locale,
          relativeTimeFormat(preferences.locale, { numeric: 'auto' }).format(
            offset,
            'day'
          )
        )
      : formatDate(preferences, value);

  return `${head}${dateTimeJoinerOf(preferences.locale)}${formatTime(preferences, value)}`;
};

/**
 * Under this, the ladder says `Just Now` rather than counting.
 *
 * Sixty, where {@link formatRelativeTime} uses forty-five. The two answer different questions and
 * are allowed to disagree: this is the threshold the screens this replaces have always had, and
 * moving either to match the other would reword a screen nobody asked to change.
 */
const RECENCY_JUST_NOW_SECONDS = 60;

/** Past this, a count of hours gives way to the wall clock. */
const RECENCY_ELAPSED_SECONDS = 4 * 3600;

/**
 * How recently something happened, on the four-rung ladder this product has always shown.
 *
 * `Just Now` under a minute, then a count out to four hours, then the wall clock labelled with its
 * day, then the timestamp. Each rung answers the question the previous one has stopped answering
 * well: a reader glancing at a last-seen column wants "is this live?" first and "when exactly?"
 * only once the answer to the first is no.
 *
 * Built from the existing formatters rather than beside them. The second rung *is*
 * {@link formatRelativeTime} and the fourth *is* {@link formatDateTime}, so a change to either
 * reaches here and the two cannot drift.
 *
 * Written for instants in the past. One in the future reads as `Just Now`, which is what the
 * screens this replaces do, and what clock skew on a device's last-seen time should look like.
 *
 * @param preferences - Supplies the locale, the time zone and the clock.
 * @param value - The instant that elapsed.
 * @param now - The instant to measure from. Defaults to the current time; pass one in tests.
 * @returns The rung that fits, such as `Just Now`, `2 hours ago` or `Today at 8:00 AM`.
 */
export const formatRecency = (
  preferences: PreferenceValues,
  value: Date,
  now: Date = new Date()
): string => {
  if (!isFiniteDate(value) || !isFiniteDate(now)) return INVALID_DATE;

  const elapsed = (now.getTime() - value.getTime()) / 1000;

  if (elapsed < RECENCY_JUST_NOW_SECONDS)
    return phraseIn(preferences.locale, 'justNow');

  if (elapsed < RECENCY_ELAPSED_SECONDS)
    return formatRelativeTime(preferences, value, now);

  const offset = daysBetween(
    dayKeyIn(preferences.timeZone, now),
    dayKeyIn(preferences.timeZone, value)
  );

  return offset >= -CALENDAR_WORD_RANGE
    ? formatCalendarTime(preferences, value, now)
    : formatDateTime(preferences, value);
};

/** Largest first, because the two shown are the two most significant that are not zero. */
const DURATION_UNITS = [
  { key: 'days', unit: 'day', seconds: 86400 },
  { key: 'hours', unit: 'hour', seconds: 3600 },
  { key: 'minutes', unit: 'minute', seconds: 60 },
  { key: 'seconds', unit: 'second', seconds: 1 },
] as const;

/** Two, because `1h 48m` is the granularity a reader acts on and `1h 48m 12s` is noise. */
const DURATION_FIELDS = 2;

type DurationParts = { key: string; unit: string; amount: number }[];

/** Empty for a duration of nothing, which the two renderers below disagree about. */
const splitDuration = (totalSeconds: number): DurationParts => {
  const parts: DurationParts = [];
  let remaining = Math.floor(Math.abs(totalSeconds));

  for (const { key, unit, seconds } of DURATION_UNITS) {
    const amount = Math.floor(remaining / seconds);
    remaining -= amount * seconds;

    // A zero unit is dropped wherever it falls, so an hour and a second reads `1h 1s` rather than
    // spending one of its two slots saying nothing.
    if (amount === 0) continue;

    parts.push({ key, unit, amount });
    if (parts.length === DURATION_FIELDS) break;
  }

  return parts;
};

/** What a duration of nothing is called, since it has no non-zero unit to name itself with. */
const ZERO_DURATION: DurationParts = [
  { key: 'seconds', unit: 'second', amount: 0 },
];

/**
 * The same phrase `Intl.DurationFormat` produces, composed from formatters that have shipped for
 * years.
 *
 * `hour`, `minute`, `second` and `day` are all sanctioned units. Callers pick `listStyle`
 * explicitly: `'narrow'` for a bare sequence (`5 hours 38 minutes`, `5h 38m`), `'long'` when the
 * join should match long unit wording (`3 years, 3 months`).
 */
const composeDuration = (
  locale: string,
  parts: DurationParts,
  unitDisplay: 'long' | 'short' | 'narrow',
  listStyle: 'long' | 'short' | 'narrow'
): string =>
  listFormat(locale, {
    style: listStyle,
    type: 'unit',
  }).format(
    parts.map(({ unit, amount }) =>
      numberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay,
        maximumFractionDigits: 0,
      }).format(amount)
    )
  );

/**
 * A length of time.
 *
 * Uses `Intl.DurationFormat` where the engine has it, and composes the same string from
 * `NumberFormat` and `ListFormat` where it does not. The two agree exactly, which a test asserts
 * rather than assumes, so a browser without the newer API is not a browser with different wording.
 *
 * @param preferences - Supplies the locale.
 * @param totalSeconds - The length, in seconds. Negative input is read as its magnitude.
 * @param options.style - Unit wording. Defaults to `narrow` (`1h 48m`); `long` is
 *   `1 hour 48 minutes`.
 * @returns A phrase showing the two most significant non-zero units.
 */
export const formatDuration = (
  preferences: PreferenceValues,
  totalSeconds: number,
  options?: { style?: 'long' | 'short' | 'narrow' }
): string => {
  const style = options?.style ?? 'narrow';

  if (!Number.isFinite(totalSeconds)) {
    return numberFormat(preferences.locale).format(totalSeconds);
  }

  const parts = splitDuration(totalSeconds);

  // `Intl.DurationFormat` renders a duration whose every field is zero as the empty string, so a
  // charge session that has not started would vanish from its own cell. Composed instead, which
  // says `0s` / `0 seconds`.
  if (parts.length === 0) {
    return composeDuration(preferences.locale, ZERO_DURATION, style, 'narrow');
  }

  // Prefer the composed path for long/short so English stays comma-free (`5 hours 38 minutes`).
  // Native `DurationFormat` is used for `narrow`, where the two paths match byte-for-byte.
  const native =
    style === 'narrow'
      ? durationFormat(preferences.locale, { style })
      : undefined;

  if (native === undefined) {
    return composeDuration(preferences.locale, parts, style, 'narrow');
  }

  return native.format(
    Object.fromEntries(parts.map(({ key, amount }) => [key, amount]))
  );
};

/** Any duration carrying all three fields serves: only the punctuation between them is read. */
const CLOCK_REFERENCE = { hours: 1, minutes: 2, seconds: 3 } as const;

/** The colon in `4:32`, which is a full stop in Danish and Finnish. */
const FALLBACK_CLOCK_SEPARATOR = ':';

/** Every digit `Intl` might render, so the punctuation is what is left when they are gone. */
const ANY_DIGIT = /\p{Nd}/gu;

/**
 * What the locale puts between the fields of a clock reading.
 *
 * Not the same character as the one between the fields of a *time*: ja-JP writes half past eight
 * as `8時30分` and yet writes half an hour of video as `30:00`, so a separator lifted from
 * `formatTime` would be wrong on a scrubber. This one comes from the digital duration pattern,
 * which is the pattern being reproduced.
 */
const clockSeparatorOf = (locale: string): string => {
  const native = durationFormat(locale, { style: 'digital' });

  if (native === undefined) return FALLBACK_CLOCK_SEPARATOR;

  return (
    native.format(CLOCK_REFERENCE).replace(ANY_DIGIT, '').slice(0, 1) ||
    FALLBACK_CLOCK_SEPARATOR
  );
};

/**
 * A length of time on a clock face: `4:32`, `1:02:33`.
 *
 * What a video scrubber, a clip's timeline and a countdown all show, and the one duration shape
 * `formatDuration` cannot express — `4m 32s` is unreadable against a play head.
 *
 * Composed rather than delegated, which is the opposite of what {@link formatDuration} does, and
 * for a stated reason: `Intl.DurationFormat`'s digital style pads every field, so four and a half
 * minutes reads `04:32`. Every scrubber in this product says `4:32` today, and a leading zero is
 * not information a reader was missing. So the padding is this function's and the separator and
 * the digits are still CLDR's — an ar-EG reader gets `٤:٣٢`.
 *
 * Hours accumulate rather than rolling into days: a nine-hour recording reads `9:00:00`, and one
 * of thirty hours reads `30:00:00`, because a play head does not know about days.
 *
 * @param preferences - Supplies the locale.
 * @param totalSeconds - The length, in seconds. Negative input is read as its magnitude, and a
 *   fraction is floored: a play head at 4.7 seconds is at `0:04`, not `0:05`.
 * @returns The reading, such as `4:32`. The hours field is shown only when it is not zero.
 */
export const formatClockDuration = (
  preferences: PreferenceValues,
  totalSeconds: number
): string => {
  const { locale } = preferences;

  if (!Number.isFinite(totalSeconds)) {
    return numberFormat(locale).format(totalSeconds);
  }

  const total = Math.floor(Math.abs(totalSeconds));
  const fields = [
    Math.floor(total / 3600),
    Math.floor(total / 60) % 60,
    total % 60,
  ];

  return fields
    .slice(fields[0] === 0 ? 1 : 0)
    .map((amount, index) =>
      numberFormat(locale, {
        useGrouping: false,
        // The leading field is unpadded and the rest are not optional: `1:02:33` needs both, and
        // `4:32` needs the minutes bare. Which field leads is what decides.
        ...(index === 0 ? {} : { minimumIntegerDigits: 2 }),
      }).format(amount)
    )
    .join(clockSeparatorOf(locale));
};

/**
 * An ISO-8601 duration, as authored: `P39M`, `P2Y`, `PT30M`.
 *
 * The date half rejects nothing and the time half follows a `T`, so the `M` in `P39M` is months
 * and the one in `PT39M` is minutes.
 */
const ISO_DURATION =
  /^P(?!$)(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?!$)(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/;

/**
 * An authored duration split into the units it should be read in, largest first.
 *
 * Twelve months make a year and twenty-four hours make a day, and both of those are definitions
 * rather than measurements, so normalising inside each half is exact. Crossing between the halves
 * is not: a month is 28, 29, 30 or 31 days depending on which one it is, so a term of months has
 * no length in days until a calendar says which months. Nothing here crosses.
 */
const splitIsoDuration = (
  value: string,
  fields: number
): DurationParts | undefined => {
  const matched = ISO_DURATION.exec(value.trim());

  if (!matched) return undefined;

  const at = (index: number): number => Number(matched[index] ?? 0);

  let seconds = at(7);
  let minutes = at(6) + Math.floor(seconds / 60);
  let hours = at(5) + Math.floor(minutes / 60);

  seconds %= 60;
  minutes %= 60;

  const days = at(4) + at(3) * 7 + Math.floor(hours / 24);
  const months = at(2);

  hours %= 24;

  const everyUnit = [
    { key: 'years', unit: 'year', amount: at(1) + Math.floor(months / 12) },
    { key: 'months', unit: 'month', amount: months % 12 },
    { key: 'days', unit: 'day', amount: days },
    { key: 'hours', unit: 'hour', amount: hours },
    { key: 'minutes', unit: 'minute', amount: minutes },
    { key: 'seconds', unit: 'second', amount: seconds },
  ];

  const named = everyUnit.filter(({ amount }) => amount > 0);

  if (named.length > 0) return named.slice(0, fields);

  // Every field is zero, so there is no most significant unit to pick. The one the author wrote
  // down is the one they meant: `P0D` is no days rather than no seconds. Weeks and days share a
  // slot, having been added together above.
  const slotOfGroup = [0, 1, 2, 2, 3, 4, 5];
  const written = slotOfGroup.findIndex(
    (_slot, group) => matched[group + 1] !== undefined
  );

  return [everyUnit[slotOfGroup[Math.max(written, 0)]]];
};

export type IsoDurationOptions = {
  /**
   * How many units to name, largest first. Defaults to two.
   *
   * Two is what a reader comparing terms down a column wants, and is why it is the default. A
   * screen that already states a length to the day — a tenure, a warranty remaining — asks for
   * three rather than dropping the field it was showing.
   */
  readonly fields?: number;
};

/**
 * A length of time that was written down rather than measured: a contract term, a plan period.
 *
 * Separate from {@link formatDuration} because that one counts seconds, and the units this reads
 * cannot be reached from seconds. `P39M` is thirty-nine months, and until a calendar says which
 * thirty-nine it is not any number of days — flattened through a thirty-day month it renders as
 * `1,170 days` where the term says `3 years, 3 months`.
 *
 * Spelled in full rather than narrowed, because these appear as a column of their own where the
 * reader is comparing terms, not glancing at an elapsed time.
 *
 * @param preferences - Supplies the locale.
 * @param value - An ISO-8601 duration, such as `P39M`.
 * @param options - How many units to name.
 * @returns A phrase such as `3 years, 3 months`, showing the most significant non-zero units up to
 *   the requested count. A string that is not an ISO-8601 duration is handed back untouched, as
 *   `formatPhone` does with a value that is not a valid number.
 */
export const formatIsoDuration = (
  preferences: PreferenceValues,
  value: string,
  options?: IsoDurationOptions
): string => {
  const parts = splitIsoDuration(value, options?.fields ?? DURATION_FIELDS);

  if (parts === undefined) return value;

  const native = durationFormat(preferences.locale, { style: 'long' });

  // `Intl.DurationFormat` renders a duration whose every field is zero as the empty string, as it
  // does for the seconds formatter above, so a nil term would vanish from its own column.
  if (native === undefined || parts.every(({ amount }) => amount === 0)) {
    return composeDuration(preferences.locale, parts, 'long', 'long');
  }

  return native.format(
    Object.fromEntries(parts.map(({ key, amount }) => [key, amount]))
  );
};
